/**
 * The automated mini-bunker (02 §9.2, mission 7). A motion detector, not a sentry: while it
 * is live it fires on ANYTHING inside 14 m, in line of sight, moving faster than a crawl -
 * the ranger and its own garrison alike. It knows nothing of uniforms, trenches or the alert.
 * Freeze, or go prone, and it loses you; it sleeps 4 s in every 10.
 *
 * Fields, for the renderer and audio: `peeking` = live (antenna up, the whine); `stateTicks`
 * = ticks left in the present phase; `burstLeft > 0` = firing; `facing` = where the gun points.
 * The phase is a pure function of the world tick (`bunkerPhase`), so nothing can knock it out
 * of step - `damageEnemy` scribbles on `stateTicks` whenever a round pings off the armour.
 */

import { makeNoise } from '../../alert';
import { type Ctx, isFighter } from '../../ctx';
import { damageEnemy } from '../../damage';
import { rangerOnFoot } from '../../hazards/terrain';
import { atan2, cos, PI, sin } from '../../math';
import { spawnBullet } from '../../projectiles';
import { clearSight } from '../../smoke';
import { ENEMY, SPECIALS } from '../../tuning';
import type { Enemy } from '../../types';
import { turnToward } from '../movement';
import { isAutomated, restoreArmour, speedOf } from './kinds';

const DEG = PI / 180;
const SENSOR_HEIGHT = 1.0;

/** Live or dormant at this tick, and for how much longer. Bunkers are staggered by id. */
export function bunkerPhase(tick: number, e: Enemy): { live: boolean; left: number } {
  const cycle = SPECIALS.bunkerLiveTicks + SPECIALS.bunkerDormantTicks;
  const t = (tick + e.id * 37) % cycle;
  const live = t < SPECIALS.bunkerLiveTicks;
  return { live, left: (live ? SPECIALS.bunkerLiveTicks : cycle) - t };
}

interface Mover {
  x: number;
  z: number;
  /** null = the ranger. */
  soldier: Enemy | null;
}

/** The nearest thing in the detector's radius, in line of sight, moving faster than a crawl. */
function nearestMover(ctx: Ctx, e: Enemy): Mover | null {
  const w = ctx.world;
  let best: Mover | null = null;
  let bestD = SPECIALS.bunkerRadius * SPECIALS.bunkerRadius;
  const consider = (x: number, z: number, height: number, soldier: Enemy | null): void => {
    const d = (x - e.x) * (x - e.x) + (z - e.z) * (z - e.z);
    if (d > bestD || !clearSight(ctx, e.x, e.z, SENSOR_HEIGHT, x, z, height)) return;
    bestD = d;
    best = { x, z, soldier };
  };
  const r = w.ranger;
  if (rangerOnFoot(w) && speedOf(r) > SPECIALS.motionSpeed) consider(r.x, r.z, r.stance === 'crawl' ? 0.4 : 1.4, null);
  for (const o of w.enemies) {
    if (o.id === e.id || !isFighter(o) || isAutomated(o.kind) || o.mans !== null) continue;
    if (speedOf(o) > SPECIALS.motionSpeed) consider(o.x, o.z, 1.4, o);
  }
  return best;
}

function fire(ctx: Ctx, e: Enemy, target: Mover): void {
  const dx = target.x - e.x;
  const dz = target.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);
  const a = atan2(dx, dz) + (ctx.rng.float() + ctx.rng.float() - 1) * SPECIALS.bunkerSpreadDeg * DEG;
  const dirX = sin(a);
  const dirZ = cos(a);
  const muzzle = SPECIALS.bunkerBody;
  if (!target.soldier) {
    spawnBullet(ctx, false, e.x + dirX * muzzle, e.z + dirZ * muzzle, dirX, dirZ, ENEMY.bulletRange, -1, true);
    // Only what it does to the ranger is the garrison's business; it mows its own men down unremarked.
    makeNoise(ctx, e.x, e.z, SPECIALS.bunkerNoise, 'gunfire', e.id);
    return;
  }
  // Enemy rounds only ever strike the ranger (projectiles.ts), so against its own side the round is for
  // show - spent where the man stands - and the hit is settled here.
  spawnBullet(ctx, false, e.x + dirX * muzzle, e.z + dirZ * muzzle, dirX, dirZ, Math.max(0.5, dist - muzzle), -1, true);
  if (!ctx.rng.chance(SPECIALS.bunkerTroopHitChance)) return;
  const s = target.soldier;
  const { detection, targetX, targetZ } = s;
  damageEnemy(ctx, s, 1, false);
  // `damageEnemy` points a hit man at the ranger; this one has no idea the ranger exists.
  s.detection = detection;
  s.targetX = targetX;
  s.targetZ = targetZ;
}

export function stepMiniBunker(ctx: Ctx, e: Enemy): void {
  restoreArmour(e);
  e.moving = false;
  const phase = bunkerPhase(ctx.world.tick, e);
  e.peeking = phase.live;
  e.stateTicks = phase.left;
  const target = phase.live ? nearestMover(ctx, e) : null;
  if (!target) {
    // Nothing moving (or asleep): the burst is cut short. Freezing works.
    e.burstLeft = 0;
    return;
  }
  e.targetX = target.x;
  e.targetZ = target.z;
  turnToward(e, atan2(target.x - e.x, target.z - e.z), SPECIALS.bunkerTraverse);
  if (e.fireCooldown > 0) return;
  if (e.burstLeft <= 0) {
    // Lock-on: the beat between the whine finding you and the first round.
    e.burstLeft = SPECIALS.bunkerBurst;
    e.fireCooldown = SPECIALS.bunkerPauseTicks;
    return;
  }
  fire(ctx, e, target);
  e.burstLeft--;
  e.sinceShot = 0;
  e.fireCooldown = SPECIALS.bunkerTicksPerShot;
}
